// @ts-check
/** @typedef {import('../../lib/schemas').ResolvedImage} ResolvedImage */
import React from 'react'
import { useLcpSrc } from '../LcpContext'
import { resolveImgRender, warnIfUnresolvedImage } from './resolve'

// The ONE presentational image component (task #67 framework), CLIENT variant.
//
// <Img> consumes a pre-resolved ResolvedImage DESCRIPTOR (built server-side by
// lib/resolveImage.js) and renders a plain <img srcSet sizes width height loading
// fetchPriority decoding> inside a CSS blur-up wrapper. It imports NOTHING that
// pulls in the asset manifest — it only reads the descriptor — so it adds zero
// manifest bytes to the client bundle.
//
// As of #129 the size/srcset/priority/reserveSpace logic lives ONCE in
// components/Img/resolve.js (shared, pure). This component is a THIN wrapper: its
// ONLY job beyond the resolver is the client-only bit — reading the page LCP src
// from LcpContext via useLcpSrc() and threading it in as `lcpSrc`. The RSC twin
// components/Img/Server.jsx is the same wrapper minus that hook, taking `lcpSrc`
// as a prop instead. Both call the SAME resolver, so their markup is identical
// by construction (see components/Img/__tests__/Server.test.js parity suite).
//
// @param {ResolvedImage|null} image  a pre-RESOLVED descriptor ({ src, srcSet?,
//   width?, height?, aspectRatio?, placeholderUrl?, alt? }) or null (renders null).
//   NOT a bare string — passing a string is the "[object Object]" regression this
//   type makes a tsc error.
// @param {string} sizes      responsive `sizes` attribute (default '100vw')
// @param {boolean} priority  ADVISORY intent hint only (#118). Accepted for API
//   compatibility but does NOT itself force eager: the LCP resolver — not the call
//   site — decides eagerness. When this <Img>'s src matches the LcpContext src
//   (the page LCP, resolved server-side by lib/lcpImage.js, task #88) it renders
//   eager + fetchPriority=high; otherwise native loading="lazy". Centralizing the
//   decision is deliberate: no call site can over-eager decorative images and
//   flood the critical path with React-Float <link preload>s (the home-FCP
//   regression class). The prop is now effectively dead; #129 keeps it accepted
//   for source-compat and drops it before the resolver.
// @param {string} objectFit  CSS object-fit (default 'contain')
// @param {string} layout     'fill' (absolute-fill the wrapper) | 'responsive'
//                            (width:100%, height:auto). Default 'fill'.
// @param {boolean} reserveSpace  fill-mode CLS control (default
//   DEFAULT_RESERVE_SPACE = true, shared with <ImgServer> — see resolve.js). When
//   the descriptor has dimensions the fill wrapper reserves the aspect-ratio box
//   from first paint so the image can't shift layout on load. Pass the explicit,
//   greppable `reserveSpace={false}` when the PARENT slot already owns the box
//   (ShopItem thumbnail, HomeSection hero, ...) — then the wrapper just fills the
//   parent. This opt-out is intentionally explicit so a client→server conversion
//   that forgets it is caught by the missing prop, not a silent layout change.
// @param {string} className  applied to the wrapper
// @param {string} alt        overrides image.alt
/**
 * @param {object} props
 * @param {ResolvedImage|null|undefined} props.image
 * @param {string} [props.sizes]
 * @param {boolean} [props.priority]
 * @param {import('react').CSSProperties['objectFit']} [props.objectFit]
 * @param {string} [props.layout]
 * @param {boolean} [props.reserveSpace]
 * @param {string} [props.className]
 * @param {string} [props.alt]
 */
function Img({
  image,
  sizes,
  // `priority` is ADVISORY/ignored (#118); destructured ONLY to keep it out of
  // `...rest` so it never lands on the <img>. (Unused rest sibling — allowed.)
  priority,
  objectFit,
  layout,
  reserveSpace,
  className,
  alt,
  ...rest
}) {
  // The ONLY client-specific step: the page LCP hero src rides LcpContext
  // (derived server-side by lib/lcpImage.js, task #88). Hydration-safe: the
  // context value is the same on server and client. useContext (via useLcpSrc)
  // is a valid unconditional hook call before the resolver's early return.
  const lcpSrc = useLcpSrc()

  warnIfUnresolvedImage(image, 'Img')
  const model = resolveImgRender({ image, sizes, lcpSrc, objectFit, layout, reserveSpace, className, alt })
  if (!model) return null

  return (
    <div className={model.wrapperClassName} style={model.wrapperStyle}>
      {/* This IS the framework <Img> component: the ONE place a raw <img> is intended.
          `alt` rides model.img (resolved by resolveImgRender), so it is invisible to
          the static a11y check through the spread. */}
      {/* eslint-disable-next-line @next/next/no-img-element, local-rules/prefer-img-component, jsx-a11y/alt-text */}
      <img {...model.img} {...rest} />
    </div>
  )
}

export default Img
